/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-params */
/* eslint-disable sonarjs/cognitive-complexity */
import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useTypedSelector } from '../../stateStore'
import { reduxServices } from '../../reducers/reduxServices'
import {
  CachedPage,
  PageConfig,
  PageDefaultParameterType,
} from '../../reducers/appSlice'

export type UsePaginationType2 = {
  newCachedPage: CachedPage
  storedPageCache: PageConfig
  paginationHasReset: HasResetType
  resetPagination: ResetAllCallbackType
  resetPaginationAt: ResetCallAtbackType
  routeOriginState: RouteOriginStateType
}

type FunctionType = () => void
export type HasResetType = () => boolean
export type ResetAllCallbackType = (identifier?: string) => void
export type ResetCallAtbackType = (
  pageNumber: number,
  pageSize: number,
  identifier?: string,
) => void
export type RouteOriginStateType = () => OriginState

const range = (start: number, end: number) => {
  const length = end - start + 1
  return Array.from({ length }, (_, idx) => idx + start)
}

export type PaginationOptionType = {
  totalItemCount: number
  customPageSize: number
  expectedOrigin: string[]
  initialPage: number
  callback: FunctionType
}

export enum OriginState {
  parentPage = 'parent',
  innerPage = 'inner',
  externalPage = 'external',
}

export const usePaginationRevised = (
  option: PaginationOptionType,
): UsePaginationType2 => {
  const defaultParam: PageDefaultParameterType = {
    initialPage: option.initialPage,
    customPageSize: option.customPageSize,
  }

  const {
    otherCaches,
    filteredCache,
    origin,
    recentCache,
    isLocationPaged,
    initialCache,
    newCustomPageSize,
    storedCache,
  } = useTypedSelector(
    reduxServices.app.selectors.selectCachedPageConfig(
      window.location.pathname,
      option.expectedOrigin,
      defaultParam,
    ),
  )

  useEffect(() => {
    const newCache: CachedPage = {
      locationPath: option.expectedOrigin[0],
      pageNumber: storedCache.page,
      pageSize: storedCache.size,
      range: [],
    }
    const allCaches = [...otherCaches, newCache]
    dispatch(reduxServices.app.actions.setCachedPages(allCaches))
  }, [storedCache])

  useEffect(() => {
    dispatch(
      reduxServices.app.actions.setReRouteLocation(option.expectedOrigin[0]),
    )

    return () => {
      dispatch(reduxServices.app.actions.setReRouteLocation(''))
    }
  }, [])

  const [canUpdateRecentPageWithList, setCanUpdateRecentPageWithList] =
    useState<number[]>([])
  const dispatch = useAppDispatch()

  const [newCachedPage, setNewCachedPage] = useState<CachedPage>(
    {} as CachedPage,
  )

  const [routeOrigin, setRouteOrigin] = useState<OriginState>(
    OriginState.externalPage,
  )

  const isRecent = recentCache.locationPath === option.expectedOrigin[0]
  const hasRecent = isRecent && !canUpdateRecentPageWithList
  const filterCache = filteredCache.length ? filteredCache[0] : initialCache
  const pageCache = hasRecent ? recentCache : filterCache

  const paginationRange = useMemo(() => {
    const end = Math.ceil(option.totalItemCount / storedCache.size)
    const pagination = range(1, end)
    if (pagination !== pageCache.range) {
      setCanUpdateRecentPageWithList(pagination)
    }
    return pagination
  }, [option.totalItemCount, newCustomPageSize, storedCache])

  const refactoredCachedPage: CachedPage = {
    locationPath: option.expectedOrigin[0],
    pageNumber: isLocationPaged ? option.initialPage : storedCache.page,
    pageSize: isLocationPaged ? option.customPageSize : storedCache.size,
    range: paginationRange,
  }

  useEffect(() => {
    dispatch(
      reduxServices.app.actions.setCurrentPagination(
        canUpdateRecentPageWithList,
      ),
    )
  }, [canUpdateRecentPageWithList])

  useEffect(() => {
    if (isLocationPaged && option.callback) {
      dispatch(
        reduxServices.app.actions.setStoredCache({
          page: option.initialPage,
          size: option.customPageSize,
        }),
      )
      option.callback()
    }
  }, [isLocationPaged])

  useEffect(() => {
    setNewCachedPage(refactoredCachedPage)
  }, [
    refactoredCachedPage.pageNumber,
    refactoredCachedPage.pageSize,
    isLocationPaged,
  ])

  useEffect(() => {
    if (option.expectedOrigin.includes(origin)) {
      if (option.expectedOrigin[0] === origin)
        setRouteOrigin(OriginState.parentPage)
      else {
        setRouteOrigin(OriginState.innerPage)
      }
      dispatch(
        reduxServices.app.actions.setReRouteLocation(option.expectedOrigin[0]),
      )
    }
  }, [newCachedPage])

  const resetPagination = (identifier?: string) => {
    const resetCachePage: CachedPage = {
      locationPath: option.expectedOrigin[0],
      pageNumber: option.initialPage,
      pageSize: option.customPageSize,
      range: paginationRange,
    }

    dispatch(reduxServices.app.actions.setRecentPageWithList(resetCachePage))
    dispatch(
      reduxServices.app.actions.setStoredCache({
        page: resetCachePage.pageNumber,
        size: resetCachePage.pageSize,
      }),
    )
    setNewCachedPage(resetCachePage)
    // if (identifier) console.info('has reset at: ', identifier)
  }
  const resetPaginationAt = (
    pageNumber: number,
    pageSize: number,
    identifier?: string,
  ) => {
    const resetCachePage: CachedPage = {
      locationPath: option.expectedOrigin[0],
      pageNumber,
      pageSize,
      range: paginationRange,
    }
    dispatch(reduxServices.app.actions.setRecentPageWithList(resetCachePage))
    dispatch(
      reduxServices.app.actions.setStoredCache({
        page: resetCachePage.pageNumber,
        size: resetCachePage.pageSize,
      }),
    )
    setNewCachedPage(resetCachePage)
    if (identifier) console.info('has reset at: ', identifier)
  }

  const paginationHasReset = () => {
    return !!isLocationPaged
  }

  const routeOriginState = () => {
    return routeOrigin
  }

  return {
    newCachedPage,
    storedPageCache: storedCache,
    paginationHasReset,
    resetPagination,
    resetPaginationAt,
    routeOriginState,
  }
}
